import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/de';

moment.locale('de');
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import Spinner from '../components/Spinner';
import './AppointmentDetail.css';

function AppointmentDetail({ tenantId }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [appointment, setAppointment] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (tenantId && id) {
      loadAppointment();
    }
  }, [tenantId, id]);

  const loadAppointment = async () => {
    try {
      setLoading(true);
      const apt = await api.get(`/appointments/${id}`, tenantId);
      setAppointment(apt);

      if (apt.customerId) {
        try {
          const cust = await api.get(`/customers/${apt.customerId}`, tenantId);
          setCustomer(cust);
        } catch (err) {
          console.error('Error loading customer:', err);
        }
      }

      if (apt.carId) {
        try {
          const vehicle = await api.get(`/cars/${apt.carId}`, tenantId);
          setCar(vehicle);
        } catch (err) {
          console.error('Error loading car:', err);
        }
      }
    } catch (error) {
      console.error('Error loading appointment:', error);
      showNotification('Fehler beim Laden des Termins', 'error');
      navigate('/appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!window.confirm('Möchten Sie diesen Termin wirklich bestätigen?')) {
      return;
    }

    try {
      setActionLoading(true);
      await api.put(`/appointments/${id}/confirm`, {}, tenantId);
      showNotification('Termin erfolgreich bestätigt', 'success');
      loadAppointment();
    } catch (error) {
      showNotification(error.message || 'Fehler beim Bestätigen des Termins', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Möchten Sie diesen Termin wirklich absagen?')) {
      return;
    }

    try {
      setActionLoading(true);
      await api.put(`/appointments/${id}/cancel`, {}, tenantId);
      showNotification('Termin erfolgreich abgesagt', 'success');
      loadAppointment();
    } catch (error) {
      showNotification(error.message || 'Fehler beim Absagen des Termins', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Möchten Sie diesen Termin wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return;
    }

    try {
      setActionLoading(true);
      await api.delete(`/appointments/${id}`, tenantId);
      showNotification('Termin erfolgreich gelöscht', 'success');
      navigate('/appointments');
    } catch (error) {
      showNotification(error.message || 'Fehler beim Löschen des Termins', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Ausstehend';
      case 'confirmed': return 'Bestätigt';
      case 'cancelled': return 'Abgesagt';
      default: return status;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'confirmed': return 'status-confirmed';
      case 'cancelled': return 'status-cancelled';
      default: return '';
    }
  };

  if (loading) {
    return <Spinner />;
  }

  if (!appointment) {
    return (
      <div className="appointment-detail">
        <div className="error-message">Termin nicht gefunden</div>
        <button onClick={() => navigate('/appointments')} className="btn-primary">
          Zurück zu Terminen
        </button>
      </div>
    );
  }

  return (
    <div className="appointment-detail">
      <div className="appointment-detail-header">
        <button onClick={() => navigate('/appointments')} className="btn-back">
          ← Zurück
        </button>
        <h1>Termin Details</h1>
      </div>

      <div className="appointment-detail-content">
        <div className="detail-section">
          <h2>Grundinformationen</h2>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Datum & Uhrzeit</label>
              <div className="detail-value">
                {moment(appointment.dateTime).format('DD.MM.YYYY HH:mm')}
              </div>
            </div>
            <div className="detail-item">
              <label>Status</label>
              <div className={`detail-value status-badge ${getStatusClass(appointment.status)}`}>
                {getStatusLabel(appointment.status)}
              </div>
            </div>
            {appointment.description && (
              <div className="detail-item full-width">
                <label>Beschreibung</label>
                <div className="detail-value">{appointment.description}</div>
              </div>
            )}
          </div>
        </div>

        <div className="detail-section">
          <h2>Kunde</h2>
          {customer ? (
            <div className="detail-grid">
              <div className="detail-item">
                <label>Name</label>
                <div className="detail-value">
                  <Link to={`/customers/${customer.id}`} className="link">
                    {customer.firstName} {customer.lastName}
                  </Link>
                </div>
              </div>
              {customer.email && (
                <div className="detail-item">
                  <label>E-Mail</label>
                  <div className="detail-value">
                    <a href={`mailto:${customer.email}`}>{customer.email}</a>
                  </div>
                </div>
              )}
              {customer.phone && (
                <div className="detail-item">
                  <label>Telefon</label>
                  <div className="detail-value">
                    <a href={`tel:${customer.phone}`}>{customer.phone}</a>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="detail-value">Kunde nicht gefunden</div>
          )}
        </div>

        {car && (
          <div className="detail-section">
            <h2>Fahrzeug</h2>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Fahrzeug</label>
                <div className="detail-value">
                  <Link to={`/cars/${car.id}`} className="link">
                    {car.make} {car.model} {car.year && `(${car.year})`}
                  </Link>
                </div>
              </div>
              {car.licensePlate && (
                <div className="detail-item">
                  <label>Kennzeichen</label>
                  <div className="detail-value">{car.licensePlate}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {appointment.confirmedAt && (
          <div className="detail-section">
            <h2>Bestätigungsinformationen</h2>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Bestätigt am</label>
                <div className="detail-value">
                  {moment(appointment.confirmedAt).format('DD.MM.YYYY HH:mm')}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="detail-section">
          <h2>Aktionen</h2>
          <div className="action-buttons">
            {appointment.status === 'pending' && (
              <button
                onClick={handleConfirm}
                disabled={actionLoading}
                className="btn-primary"
              >
                Termin bestätigen
              </button>
            )}
            {appointment.status !== 'cancelled' && (
              <button
                onClick={handleCancel}
                disabled={actionLoading}
                className="btn-secondary"
              >
                {appointment.status === 'pending' ? 'Termin absagen' : 'Termin löschen'}
              </button>
            )}
            {appointment.status === 'cancelled' && (
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="btn-danger"
              >
                Termin löschen
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AppointmentDetail;

